import {
  cellValueFromGQL,
  FormFieldValueGroupUI,
  isNoneCell,
  isNotUndefined,
  parseDate,
  RowViewState,
  UNGROUPED_CELL_ID,
} from "Backend";
import { CellAcc, CellWithAccs, GroupedValueGroupByCellValue } from "contexts/FormFieldContext";
import { Cell, CellType } from "generated/graphql";
import { sortByKeys } from "helpers/coll";

export function valueGroupDateValues(vg: FormFieldValueGroupUI): Date[] {
  return vg.values
    .filter((v) => v.value.dateValue)
    .map((v) => parseDate(v.value.dateValue))
    .filter(isNotUndefined);
}

export function anyDateInRange(dates: Date[], startDate: Date | undefined, endDate: Date | undefined): boolean {
  return (
    (startDate === undefined ? true : dates.some((d) => d.getTime() >= startDate.getTime())) &&
    (endDate === undefined ? true : dates.some((d) => d.getTime() <= endDate.getTime()))
  );
}

export function sortDates(dates: Date[]): Date[] {
  const d = dates.slice();
  d.sort((a, b) => a.getTime() - b.getTime());
  return d;
}

export function groupValueGroupByCellId(
  valueGroupsInRange: FormFieldValueGroupUI[],
  groupBy: number,
  cellByIdMap: Map<number, Cell>
): GroupedValueGroupByCellValue {
  if (groupBy === UNGROUPED_CELL_ID) {
    return {};
  }
  const group = valueGroupsInRange.reduce((acc, vg) => {
    const originalValue = cellValueFromGQL(vg.values.find((v) => v.cellId === groupBy)?.value);
    const cellNameValue = originalValue?.toString() ?? "";
    const dates = valueGroupDateValues(vg);
    const usableValues = vg.values.filter(
      (v) => cellByIdMap.get(v.cellId)?.type !== CellType.Date && !isNoneCell(v.value)
    );
    const group = acc[cellNameValue];
    if (group === undefined) {
      acc[cellNameValue] = {
        originalValue,
        cellValue: cellNameValue,
        valueGroups: [vg],
        sortedDates: sortDates(dates),
        cellAccs: usableValues.map((v) => {
          const cell = cellByIdMap.get(v.cellId);
          if (cell === undefined) {
            console.error(`Cell ${v.cellId} not found in`, cellByIdMap);
            throw new Error(`Cell ${v.cellId} not found in cells`);
          }

          return {
            cell,
            accs: [
              {
                cellValue: v.value,
                cell,
                count: 1,
              },
            ],
          };
        }),
      };
    } else {
      group.valueGroups.push({
        ...vg,
        viewState: vg.viewState ?? RowViewState.ReadCollapsed,
      });
      group.sortedDates = sortDates([...group.sortedDates, ...dates]);
      usableValues.forEach((v) => {
        const cell = cellByIdMap.get(v.cellId);
        if (isNoneCell(v.value) || cell === undefined) return;

        const accCell: CellWithAccs | undefined = group.cellAccs.find((ca) => ca.cell.name === cell.name);

        if (accCell !== undefined) {
          const foundCellValueMatch = accCell.accs.find(
            (c) => cellValueFromGQL(c.cellValue) === cellValueFromGQL(v.value)
          );
          if (foundCellValueMatch === undefined) {
            accCell.accs.push({
              cellValue: v.value,
              cell,
              count: 1,
            });
          } else {
            foundCellValueMatch.count++;
          }
        } else {
          group.cellAccs.push({
            cell,
            accs: [{ cellValue: v.value, cell, count: 1 }],
          });
        }
      });
    }
    return acc;
  }, {} as GroupedValueGroupByCellValue);
  Object.values(group).forEach((group) => {
    group.cellAccs.forEach((cellAcc) => {
      cellAcc.accs.sort((a, b) => b.count - a.count);
    });
    group.cellAccs.sort((a, b) => a.cell.rank - b.cell.rank);
  });
  return sortByKeys(group, (a, b) => {
    if (a === "") return 1;
    if (b === "") return -1;
    return a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase());
  });
}

export function splitMultiSelectCellAccs(cellAccs: CellAcc[]): CellAcc[] {
  const cell = cellAccs[0]?.cell;

  if (cell === undefined) {
    return [];
  }

  const multiSelectValues: string[][] = cellAccs.map((cellAcc) => cellAcc.cellValue.multiSelectValue ?? []);
  const map = new Map<string, { count: number }>();
  multiSelectValues.forEach((value) => {
    value.forEach((v) => {
      if (map.has(v)) {
        map.get(v)!.count++;
      } else {
        map.set(v, { count: 1 });
      }
    });
  });
  return Array.from(map.entries())
    .map(([value, { count }]) => ({
      cellValue: { textValue: value, type: CellType.Select },
      count,
      cell: { ...cell, type: CellType.Select },
    }))
    .sort((a, b) => b.count - a.count);
}

export function distinctReferencesInGroup(group: FormFieldValueGroupUI): number | undefined {
  if (group.references === undefined || group.references.length === 0) {
    return undefined;
  }
  const singlePageReferences = group.references
    .filter((r) => r.blockIndex !== undefined || r.blockIndex !== null)
    .map((r) => `${r.documentIntakeId}-${r.originalPage}`);
  const singlePageSet = new Set(singlePageReferences);
  const multiPageReferences = group.references.filter((r) => r.blockIndex === undefined || r.blockIndex === null);
  return singlePageSet.size + multiPageReferences.length;
}
